import { useRef, useState } from "react";
import { Alert, Box, Button, Divider, Stack } from "@mui/material";
import { useIsModificationReasonSatisfied } from "@src/appV2/ExperimentalHomeHealth/components/ModificationReasonField";
import { getFloatingNavigationScrollPaddingBottom } from "@src/appV2/redesign/components/FloatingNavigation/scrollPadding";
import { Title } from "@src/appV2/redesign/components/Title";
import { addHours } from "date-fns";
import { useHistory } from "react-router-dom";

import { useFormatTimestampForWorkplace } from "../api/useFormatTimestampForWorkplace";

import { useCreateCase } from "../api/useCreateCase";
import { useCreatePatient } from "../api/useCreatePatient";
import { useCreateVisit } from "../api/useCreateVisit";
import { useCanSetRates } from "../../lib/useCanSetRates";
import { useCurrentUserId } from "../../lib/useCurrentUserId";
import { PricingType, VisitStatus, VisitType, WorkerRequirements } from "../types";
import { HOME_HEALTH_CASES_PATH } from "../paths";
import { type SelectedAddress } from "./MobileAddressAutocompleteField";
import { MobileCreateCaseStep1, type Step1Data } from "./MobileCreateCaseStep1";
import { MobileCreateCaseStep2, type Step2Data } from "./MobileCreateCaseStep2";
import { MobileCreateCaseStep3, type Step3Data } from "./MobileCreateCaseStep3";
import { MobileCreateCaseSuccess } from "./MobileCreateCaseSuccess";

interface WizardFormData extends Step1Data, Step2Data, Step3Data {}

const DEFAULT_FORM_DATA: WizardFormData = {
  patientName: "",
  address: "",
  visitType: "",
  workerRequirement: "",
  pricingType: PricingType.PER_VISIT,
  visitsPerWeek: "",
  totalWeeks: "",
  firstVisitDueDate: "",
  chargePerVisit: "",
  payPerVisit: "",
  specialties: [],
  description: "",
  modificationReason: "",
};

const TOTAL_STEPS = 3;

const buttonBaseSx = {
  flex: 1,
  borderRadius: "24px",
  py: 1.5,
  textTransform: "none",
  fontSize: "1rem",
} as const;

export function MobileCreateCaseWizardContainer() {
  const history = useHistory();
  const workplaceId = useCurrentUserId() ?? "";

  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState<WizardFormData>(DEFAULT_FORM_DATA);
  const [selectedAddress, setSelectedAddress] = useState<SelectedAddress | undefined>(undefined);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const createdRef = useRef<{ patientId?: string; caseId?: string }>({});

  const { mutateAsync: createPatient, isLoading: isCreatingPatient } = useCreatePatient();
  const { mutateAsync: createCase, isLoading: isCreatingCase } = useCreateCase();
  const { mutateAsync: createVisit, isLoading: isCreatingVisit } = useCreateVisit();

  const isSubmitting = isCreatingPatient || isCreatingCase || isCreatingVisit;
  const isModificationReasonSatisfied = useIsModificationReasonSatisfied(
    formData.modificationReason
  );
  const canSetRates = useCanSetRates();
  const formatTimestampForWorkplace = useFormatTimestampForWorkplace();

  function handleChange(field: keyof WizardFormData, value: string | string[] | PricingType) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  function handleAddressSelect(address: SelectedAddress) {
    setSelectedAddress(address);
    setFormData((prev) => ({ ...prev, address: address.formattedAddress }));
  }

  // A valid address requires picking a real suggestion (so we have coordinates).
  // Editing the text after selecting makes it diverge from the picked address,
  // which invalidates it until a suggestion is selected again.
  const hasValidAddress =
    formData.address.length > 0 && selectedAddress?.formattedAddress === formData.address;

  // Block advancing/posting until the fields visible on the current step are
  // filled in, so a case can't be posted with empty data. Rate fields are only
  // required when the user can set rates (they aren't shown otherwise).
  function isStepValid(currentStep: number): boolean {
    if (currentStep === 0) {
      // Trim free-text inputs so a whitespace-only value doesn't count as filled.
      // The address must be a real picked suggestion with coordinates (see
      // hasValidAddress), not just any non-empty text.
      return (
        Boolean(formData.patientName.trim()) &&
        hasValidAddress &&
        Boolean(formData.visitType) &&
        Boolean(formData.workerRequirement)
      );
    }
    if (currentStep === 1) {
      const hasRates = canSetRates
        ? Boolean(formData.chargePerVisit) && Boolean(formData.payPerVisit)
        : true;
      return (
        Boolean(formData.visitsPerWeek) &&
        Boolean(formData.totalWeeks) &&
        Boolean(formData.firstVisitDueDate) &&
        hasRates
      );
    }
    return isModificationReasonSatisfied;
  }

  function parseDateFieldToISO(dateString: string): string {
    const [yearStr, monthStr, dayStr] = dateString.split("-");
    const localDate = new Date(Number(yearStr), Number(monthStr) - 1, Number(dayStr));
    return formatTimestampForWorkplace(addHours(localDate, 12));
  }

  async function handleSubmit() {
    setSubmitError(null);
    try {
      const { modificationReason } = formData;

      const patientId =
        createdRef.current.patientId ??
        (
          await createPatient({
            data: {
              attributes: {
                workplaceId,
                externalPatientIdentifier: formData.patientName,
                formattedAddress: formData.address,
                latitude: selectedAddress?.latitude ?? 0,
                longitude: selectedAddress?.longitude ?? 0,
                oasis: false,
                modificationReason,
              },
              type: "patient",
            },
          })
        ).id;
      createdRef.current.patientId = patientId;

      const caseId =
        createdRef.current.caseId ??
        (
          await createCase({
            data: {
              attributes: {
                patientId,
                workplaceId,
                specialties: formData.specialties,
                description: formData.description,
                modificationReason,
              },
              type: "case",
            },
          })
        ).id;
      createdRef.current.caseId = caseId;

      await createVisit({
        data: {
          attributes: {
            status: VisitStatus.OPEN,
            caseId,
            type: formData.visitType as VisitType,
            workerReq: (formData.workerRequirement as WorkerRequirements) || undefined,
            pricingType: formData.pricingType,
            interestedWorkersIds: [],
            invitedWorkersIds: [],
            invitedWorkersNames: [],
            modificationReason,
            ...(formData.visitsPerWeek
              ? { visitsPerWeek: Number.parseInt(formData.visitsPerWeek, 10) }
              : {}),
            ...(formData.totalWeeks
              ? { durationInWeeks: Number.parseInt(formData.totalWeeks, 10) }
              : {}),
            ...(formData.firstVisitDueDate
              ? { deadline: parseDateFieldToISO(formData.firstVisitDueDate) }
              : {}),
            ...(formData.chargePerVisit
              ? {
                  chargeRate: {
                    amountInMinorUnits: Math.round(
                      Number.parseFloat(formData.chargePerVisit) * 100
                    ),
                    currencyCode: "USD" as const,
                  },
                }
              : {}),
            ...(formData.payPerVisit
              ? {
                  payRate: {
                    amountInMinorUnits: Math.round(Number.parseFloat(formData.payPerVisit) * 100),
                    currencyCode: "USD" as const,
                  },
                }
              : {}),
          },
          type: "visit",
        },
      });

      setIsSuccess(true);
      createdRef.current = {};
    } catch {
      setSubmitError("Something went wrong. Please try again.");
    }
  }

  function renderStep() {
    switch (step) {
      case 0:
        return (
          <MobileCreateCaseStep1
            data={formData}
            onChange={handleChange}
            onAddressSelect={handleAddressSelect}
          />
        );
      case 1:
        return (
          <MobileCreateCaseStep2
            data={formData}
            pricingType={formData.pricingType}
            visitType={(formData.visitType as VisitType) || undefined}
            workerRequirement={(formData.workerRequirement as WorkerRequirements) || undefined}
            patientAddress={formData.address}
            onChange={handleChange}
          />
        );
      case 2:
        return <MobileCreateCaseStep3 data={formData} onChange={handleChange} />;
      default:
        return null;
    }
  }

  const paddingX = 3;

  return (
    <Stack
      sx={(theme) => ({
        minHeight: "100vh",
        bgcolor: "background.baseDark",
        // Clear the floating Menu pill and the iOS home bar so the footer
        // buttons aren't overlapped or jammed against the bottom of the screen.
        paddingBottom: getFloatingNavigationScrollPaddingBottom(theme) ?? "4rem",
      })}
    >
      <Stack sx={{ px: paddingX, pt: 4, gap: 3 }}>
        <Title component="h5">Post a new case</Title>
        <Divider />
        {!isSuccess && (
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
            {(["step-1", "step-2", "step-3"] as const).map((key, i) => (
              <Box
                key={key}
                sx={{
                  flex: 1,
                  height: 8,
                  borderRadius: 3,
                  bgcolor: i <= step ? "base.secondary" : "transparent",
                  border: "1px solid",
                  borderColor: "base.secondary",
                }}
              />
            ))}
          </Stack>
        )}
      </Stack>

      <Stack sx={{ flex: 1, overflowY: "auto", px: paddingX, py: 3 }}>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}
        {isSuccess ? <MobileCreateCaseSuccess /> : renderStep()}
      </Stack>

      <Stack direction="row" spacing={2} sx={{ px: paddingX, pb: 4, pt: 2 }}>
        {isSuccess ? (
          <Button
            fullWidth
            variant="primary"
            disabled={isSubmitting}
            onClick={() => {
              history.replace(HOME_HEALTH_CASES_PATH);
            }}
            sx={{ ...buttonBaseSx }}
          >
            OK
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              disabled={isSubmitting}
              onClick={() => {
                if (step === 0) {
                  history.goBack();
                } else {
                  setStep((s) => s - 1);
                }
              }}
              sx={{ ...buttonBaseSx }}
            >
              Back
            </Button>
            <Button
              variant="primary"
              disabled={isSubmitting || !isStepValid(step)}
              onClick={() => {
                if (step < TOTAL_STEPS - 1) {
                  setStep((s) => s + 1);
                } else {
                  void handleSubmit();
                }
              }}
              sx={{ ...buttonBaseSx }}
            >
              {step < TOTAL_STEPS - 1 ? "Next" : "Submit"}
            </Button>
          </>
        )}
      </Stack>
    </Stack>
  );
}
